import React, { useRef } from 'react'

import { DOUBLE_TAP_THRESHOLD_MS } from '../constants'

interface VertexMarkerProps {
  /** X座標 */
  cx: number
  /** Y座標 */
  cy: number
  /** 選択状態かどうか */
  isSelected: boolean
  /** この頂点が「選択中の頂点」かどうか（右クリックメニュー / Delete キー削除の対象） */
  isVertexSelected?: boolean
  /** この頂点がドラッグ中かどうか */
  isDraggingThis?: boolean
  /** ポイントのインデックス */
  pointIndex: number
  /** ポリゴンのインデックス: -1 = points (メインポリゴン), 0以上 = polygons[n] */
  polygonIndex?: number
  /** ドラッグ開始ハンドラ */
  onDragStart: (
    e: React.MouseEvent | React.TouchEvent,
    pointIndex: number,
    polygonIndex: number,
  ) => void
  /** ドラッグ終了ハンドラ */
  onDragEnd: () => void
  /** 頂点削除ハンドラ（ダブルクリック/ダブルタップ時） */
  onDeletePoint?: (pointIndex: number, polygonIndex: number) => void
  /** 右クリック（コンテキストメニュー）ハンドラ */
  onContextMenu?: (e: React.MouseEvent, pointIndex: number, polygonIndex: number) => void
  /**
   * 非選択状態 (`!isSelected`) の頂点を主ボタンで押下したときに呼ばれる（issue #2524 追加対応）。
   * 「点をクリックするだけでは動かせず、先に面をクリックして選択が必要」という 2 段階操作を
   * 緩和するためのオプトイン。未指定 (デフォルト) なら従来通り何も起きない
   * （AI カラー側は本 prop を渡さないため挙動は完全に不変）。
   */
  onSelectRequest?: () => void
  /**
   * キャンバス移動モード中に true。ヒット領域を無効化してパン操作を親に通す。
   * (親 AreaOverlay の pointerEvents: none だけではヒット円の pointerEvents: auto が
   *  ヒットテストを復活させてしまうため、頂点側でも明示的に無効化する)
   */
  interactionDisabled?: boolean
  /**
   * マーカーの表示倍率。ズーム時に 1/zoomScale を渡すことで、ヒット領域と
   * 見た目のサイズをズームに依らずスクリーン上一定に保つ
   * (これが無いと拡大時にヒット領域も拡大され、カーソルから離れた頂点が選択される)
   */
  markerScale?: number
  /** テスト用ID */
  testId?: string
}

/**
 * 頂点マーカーコンポーネント
 * 塗りつぶしの円で頂点を表示
 * ダブルクリック/ダブルタップで削除可能
 */
export const VertexMarker: React.FC<VertexMarkerProps> = ({
  cx,
  cy,
  isSelected,
  isVertexSelected = false,
  isDraggingThis = false,
  pointIndex,
  polygonIndex = -1,
  onDragStart,
  onDragEnd,
  onDeletePoint,
  onContextMenu,
  onSelectRequest,
  interactionDisabled = false,
  markerScale = 1,
  testId,
}) => {
  const lastTapRef = useRef<number>(0)
  const lastPenTapRef = useRef<number>(0)

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (isSelected && onDeletePoint) {
      onDeletePoint(pointIndex, polygonIndex)
    }
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation()
    onDragEnd()

    // ダブルタップ検出
    const now = Date.now()
    if (now - lastTapRef.current < DOUBLE_TAP_THRESHOLD_MS && isSelected && onDeletePoint) {
      onDeletePoint(pointIndex, polygonIndex)
      lastTapRef.current = 0
    } else {
      lastTapRef.current = now
    }
  }

  return (
    <g transform={`translate(${cx}, ${cy}) scale(${markerScale})`}>
      {/* インタラクティブ用の透明領域（タッチ・クリック領域を広げる） */}
      <circle
        cx={0}
        cy={0}
        fill="transparent"
        r={15}
        style={{
          cursor: 'grab',
          pointerEvents: interactionDisabled ? 'none' : 'auto',
        }}
        onContextMenu={e => {
          e.preventDefault()
          e.stopPropagation()
          if (isSelected && onContextMenu) {
            onContextMenu(e, pointIndex, polygonIndex)
          }
        }}
        onDoubleClick={handleDoubleClick}
        onMouseDown={e => {
          e.stopPropagation()
          // 右クリックはコンテキストメニュー用なのでドラッグを開始しない
          if (e.button !== 0) return
          if (isSelected) {
            onDragStart(e, pointIndex, polygonIndex)
          } else {
            onSelectRequest?.()
          }
        }}
        onMouseUp={e => {
          e.stopPropagation()
          onDragEnd()
        }}
        onPointerDown={e => {
          if (e.pointerType === 'pen') {
            e.preventDefault()
            e.stopPropagation()
            if (isSelected) {
              onDragStart(e as React.MouseEvent, pointIndex, polygonIndex)
            } else {
              onSelectRequest?.()
            }
          }
        }}
        onPointerUp={e => {
          if (e.pointerType === 'pen') {
            e.stopPropagation()
            onDragEnd()
            // ダブルタップで削除検出（pen専用refを使用し、touchとの干渉を防ぐ）
            const now = Date.now()
            if (
              now - lastPenTapRef.current < DOUBLE_TAP_THRESHOLD_MS &&
              isSelected &&
              onDeletePoint
            ) {
              onDeletePoint(pointIndex, polygonIndex)
              lastPenTapRef.current = 0
            } else {
              lastPenTapRef.current = now
            }
          }
        }}
        onTouchEnd={handleTouchEnd}
        onTouchStart={e => {
          e.stopPropagation()
          if (isSelected) {
            onDragStart(e, pointIndex, polygonIndex)
          } else {
            onSelectRequest?.()
          }
        }}
      />

      {/* 頂点マーカー（ドラッグ中は非表示。選択中の頂点は青 + 大きめに強調） */}
      {!isDraggingThis && (
        <circle
          cx={0}
          cy={0}
          data-testid={testId}
          data-vertex-selected={isVertexSelected || undefined}
          fill={isVertexSelected ? '#2563EB' : isSelected ? 'red' : 'gray'}
          pointerEvents="none"
          r={isVertexSelected ? 5 : 3}
          stroke="#fff"
          strokeWidth={isVertexSelected ? 1.5 : 1}
        />
      )}
    </g>
  )
}
